import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Input, Select, Field, Modal, Spinner, Textarea, Badge } from '../ui.jsx';

const SERVICES_BY_INDUSTRY = {
  HVAC: ['AC Repair','AC Replacement','AC Tune-Up','Furnace Repair','Furnace Replacement','Heat Pump','Mini Split','Duct Cleaning','Indoor Air Quality','Maintenance Plans'],
  Plumbing: ['Drain Cleaning','Leak Repair','Water Heater','Re-Pipe','Toilet Install','Faucet Repair','Sewer Line','Garbage Disposal','Bathroom Remodel'],
  Electrical: ['Panel Upgrade','Outlet Repair','EV Charger','Generator Install','Lighting','Safety Inspection','Whole-Home Surge Protection'],
  Roofing: ['Storm Damage','Leak Repair','Full Replacement','Inspection','Gutter Install','Skylight'],
  'Garage Door': ['Spring Replacement','Opener Install','Cable Repair','Tune-Up','Full Door Install'],
  Cleaning: ['Recurring Residential','One-time Deep Clean','Move-in/Move-out','Commercial','Carpet Cleaning'],
  Landscaping: ['Lawn Mowing','Tree Trimming','Mulching','Spring Clean-Up','Fall Clean-Up','Sprinkler Repair'],
  'General Contractor': ['Kitchen Remodel','Bath Remodel','Addition','Whole-Home','Decks','Concrete'],
};

const DAYS = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];

const STEPS = [
  { n:1, label:'Business',        icon:'🏢', help:'Legal entity + contact details' },
  { n:2, label:'Services',        icon:'🛠️', help:'What you offer + pricing' },
  { n:3, label:'Service Area',    icon:'📍', help:'Where + when you work' },
  { n:4, label:'Customer-Facing', icon:'💬', help:'Estimate/invoice copy + reviews' },
  { n:5, label:'Payments',        icon:'💳', help:'Stripe Connect for cards + ACH' },
  { n:6, label:'SMS Verify',      icon:'📱', help:'Toll-free verification for texting' },
  { n:7, label:'Team',            icon:'👥', help:'Invite owners + dispatchers + techs' },
  { n:8, label:'Terms',           icon:'📜', help:'Your customer T&C + TradeOps SaaS agreement' },
  { n:9, label:'Go Live',         icon:'🚀', help:'Final review + activate' },
];
const TOTAL_STEPS = STEPS.length;

export function OnboardingForm({ open, onClose, onSaved, toast }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [tfvStatus, setTfvStatus] = useState(null);
  const [tfvForm, setTfvForm] = useState({ useCase:'CUSTOMER_CARE', sampleMsg:'', optInUrl:'' });
  const [team, setTeam] = useState([]);
  const [newMember, setNewMember] = useState({ email:'', firstName:'', lastName:'', role:'tech', phone:'' });

  useEffect(() => {
    if (open) {
      api('/api/admin-panel/onboarding-form').then(d => {
        setData({
          ...d,
          businessHours: Object.keys(d.businessHours||{}).length ? d.businessHours : { mon:'07:00-17:00', tue:'07:00-17:00', wed:'07:00-17:00', thu:'07:00-17:00', fri:'07:00-17:00', sat:null, sun:null },
        });
      }).catch(()=>{});
      api('/api/integrations/stripe/detailed-status').then(setStripeStatus).catch(()=>setStripeStatus({connected:false}));
      api('/api/settings/tollfree-verification').then(setTfvStatus).catch(()=>setTfvStatus(null));
      api('/api/team').then(r => setTeam(Array.isArray(r) ? r : (r?.users||[]))).catch(()=>setTeam([]));
    }
  }, [open]);

  if (!open) return null;
  const step1Valid = data.name && data.phone && data.email && data.industry && data.legalName && data.ein && data.address && data.city && data.state && data.zip && data.attestStateMatch;
  const step2Valid = (data.servicesOffered||[]).length > 0;
  // Service area valid if ANY of the three filters are set — zips, radius, or state list.
  const step3Valid = (data.targetZips||[]).length > 0 || data.serviceRadiusMiles > 0 || (data.serviceStates||[]).length > 0;
  const stepValid = (n) => n===1 ? step1Valid : n===2 ? step2Valid : n===3 ? step3Valid : true;

  const save = async (markComplete) => {
    setBusy(true);
    try {
      await api('/api/admin-panel/onboarding-form', { method:'PATCH', body: { ...data, markCompleted: !!markComplete }});
      if (markComplete) toast('🎉 Account configured — auto-populated everywhere');
      else toast('Saved');
      onSaved?.();
      if (markComplete) onClose();
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const toggleService = s => {
    const cur = new Set(data.servicesOffered||[]);
    if (cur.has(s)) cur.delete(s); else cur.add(s);
    setData({...data, servicesOffered: Array.from(cur)});
  };

  const ind = data.industry || 'HVAC';
  const services = SERVICES_BY_INDUSTRY[ind] || SERVICES_BY_INDUSTRY.HVAC;

  const startStripeOnboarding = async () => {
    setBusy(true);
    try {
      await save(false);
      const r = await api('/api/integrations/stripe/express-onboarding', { method:'POST' });
      if (r?.url) window.location.href = r.url;
      else toast('Stripe returned no link — check Connect setup in env', 'error');
    } catch (e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const submitTfv = async () => {
    if (!tfvForm.sampleMsg.trim()) return toast('Add a sample message first','error');
    setBusy(true);
    try {
      await api('/api/settings/tollfree-verification', { method:'POST', body:{
        useCase: tfvForm.useCase,
        sampleMessages: [tfvForm.sampleMsg.trim()],
        optInImageUrl: tfvForm.optInUrl.trim() || null,
        optInType: 'WEB_FORM',
      }});
      toast('TFV submitted — Twilio typically reviews in 1–3 business days', 'success');
      const fresh = await api('/api/settings/tollfree-verification'); setTfvStatus(fresh);
    } catch (e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const addTeamMember = async () => {
    if (!newMember.email || !newMember.firstName) return toast('Email + first name required', 'error');
    setBusy(true);
    try {
      const r = await api('/api/team', { method:'POST', body: newMember });
      toast(`${newMember.firstName} added — temp password: ${r.tempPassword}`, 'success');
      setNewMember({ email:'', firstName:'', lastName:'', role:'tech', phone:'' });
      const fresh = await api('/api/team'); setTeam(Array.isArray(fresh) ? fresh : (fresh?.users||[]));
    } catch (e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={920}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h2 style={{ margin:0 }}>🚀 Onboarding — set up everything</h2>
      <Badge c={G.purple} bg={G.purpleDim}>Step {step} of {TOTAL_STEPS}</Badge>
    </div>

    {/* Step nav — clickable + shows ✓ for steps with required data filled */}
    <div style={{ display:'flex', gap:4, marginBottom:14, flexWrap:'wrap' }}>
      {STEPS.map(s => {
        const isCurrent = s.n === step;
        const isDone = (s.n === 1 && step1Valid) || (s.n === 2 && step2Valid) || (s.n === 3 && step3Valid)
          || (s.n === 5 && stripeStatus?.connected) || (s.n === 6 && tfvStatus?.status && tfvStatus.status !== 'not_submitted')
          || (s.n === 7 && team.length >= 1);
        return <div key={s.n} onClick={()=>setStep(s.n)} style={{
          flex:'1 1 100px', padding:'8px 6px', borderRadius:8, cursor:'pointer', textAlign:'center',
          background: isCurrent ? G.purpleDim : 'transparent',
          border:`1px solid ${isCurrent ? G.purple : (isDone ? G.green : G.cardBorder)}`,
        }}>
          <div style={{ fontSize:16 }}>{isDone ? '✅' : s.icon}</div>
          <div style={{ fontSize:10, color: isCurrent ? G.purple : G.textMuted, fontWeight:700, marginTop:2 }}>{s.label}</div>
        </div>;
      })}
    </div>

    <div style={{ height:4, background:G.input, borderRadius:2, marginBottom:18 }}>
      <div style={{ height:'100%', width:`${step/TOTAL_STEPS*100}%`, background:`linear-gradient(90deg,${G.purple},${G.cyan})`, borderRadius:2, transition:'width .3s' }}/>
    </div>

    {step === 1 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>1️⃣ Business + Legal Profile</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Legal name + EIN are required for Stripe Connect underwriting + 1099s. Stored encrypted.</div>

      {/* HARD WARNING — A2P SMS registration with Twilio FAILS if the legal
          name, EIN, or address don't match the state LLC paperwork. Make
          this impossible to miss BEFORE they type, not after rejection. */}
      <div style={{
        background: G.yellow + '11', border: `1px solid ${G.yellow}66`,
        borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.55, color: G.text,
      }}>
        <div style={{ fontWeight: 700, color: G.yellow, marginBottom: 6 }}>⚠️ Match this EXACTLY to your state LLC filing</div>
        Legal entity name, EIN, and the business address you enter here MUST match what's on file with your state (the document you received when you registered the LLC). Twilio + Stripe verify against state + IRS records — any mismatch (extra space, wrong suite number, "&" vs "and", etc.) bounces SMS registration AND payment payouts. Pull up your state's articles of incorporation / certificate of formation and copy from there.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="DBA / Business Name *" hint="What customers see"><Input value={data.name||''} onChange={e=>setData({...data, name:e.target.value})}/></Field>
        <Field label="Legal Entity Name *" hint="EXACTLY as on state LLC filing — include 'LLC', 'Inc.', etc."><Input value={data.legalName||''} onChange={e=>setData({...data, legalName:e.target.value})} placeholder="Arctic HVAC LLC"/></Field>
        <Field label="Entity Type">
          <Select value={data.entityType||'LLC'} onChange={e=>setData({...data, entityType:e.target.value})}>
            <option>LLC</option><option>S-Corp</option><option>C-Corp</option><option>Sole Proprietor</option><option>Partnership</option>
          </Select>
        </Field>
        <Field label="EIN (Federal Tax ID) *" hint="Format: XX-XXXXXXX (IRS letter)"><Input value={data.ein||''} onChange={e=>setData({...data, ein:e.target.value})} placeholder="XX-XXXXXXX" autoComplete="off"/></Field>
        <Field label="Year Founded"><Input type="number" value={data.yearFounded||''} onChange={e=>setData({...data, yearFounded: Number(e.target.value)||null})}/></Field>
        <Field label="State License Number"><Input value={data.licenseNumber||''} onChange={e=>setData({...data, licenseNumber:e.target.value})}/></Field>
        <Field label="Owner Name" hint="First + last, EXACTLY as the authorized rep on state filing"><Input value={data.ownerName||''} onChange={e=>setData({...data, ownerName:e.target.value})}/></Field>
        <Field label="Industry">
          <Select value={data.industry||'HVAC'} onChange={e=>setData({...data, industry:e.target.value})}>
            {Object.keys(SERVICES_BY_INDUSTRY).map(i => <option key={i}>{i}</option>)}
          </Select>
        </Field>
        <Field label="Email *"><Input type="email" value={data.email||''} onChange={e=>setData({...data, email:e.target.value})}/></Field>
        <Field label="Phone *" hint="E.164 format: +1XXXXXXXXXX. No dashes / parens."><Input value={data.phone||''} onChange={e=>setData({...data, phone:e.target.value})}/></Field>
        <Field label="Business Address *" style={{ gridColumn:'1/-1' }} hint="Street + suite, EXACTLY as on state LLC filing. PO Boxes are rejected — must be physical address."><Input value={data.address||''} onChange={e=>setData({...data, address:e.target.value})}/></Field>
        <Field label="City *"><Input value={data.city||''} onChange={e=>setData({...data, city:e.target.value})}/></Field>
        <Field label="State *" hint="2-letter code (OH, FL, etc.) not full name"><Input value={data.state||''} onChange={e=>setData({...data, state:e.target.value})} maxLength={2} style={{textTransform:'uppercase'}}/></Field>
        <Field label="Zip *"><Input value={data.zip||''} onChange={e=>setData({...data, zip:e.target.value})}/></Field>
      </div>

      <label style={{ display:'flex', alignItems:'flex-start', gap:8, marginTop:12, padding:10, background:G.input, borderRadius:6, cursor:'pointer' }}>
        <input type="checkbox" checked={!!data.attestStateMatch} onChange={e=>setData({...data, attestStateMatch:e.target.checked})} style={{ marginTop:3 }}/>
        <span style={{ fontSize:13, color:G.text }}>
          I confirm the legal name, EIN, and address above match my state LLC filing and IRS EIN letter exactly. I understand that mismatches cause SMS registration and Stripe payouts to be rejected.
        </span>
      </label>
    </>}

    {step === 2 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>2️⃣ Services Offered</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Tap every service you offer. These pre-populate your price book + drop-downs across the app.</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:18 }}>
        {services.map(s => {
          const on = (data.servicesOffered||[]).includes(s);
          return <div key={s} onClick={()=>toggleService(s)} style={{ padding:'8px 14px', borderRadius:18, cursor:'pointer', border:`1px solid ${on?G.purple:G.cardBorder}`, background: on?G.purpleDim:'transparent', color: on?G.purple:G.text, fontSize:13, fontWeight:600 }}>{on?'✓ ':''}{s}</div>;
        })}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
        <Field label="Standard Labor Rate ($/hr)"><Input type="number" step="0.01" value={data.laborRate||85} onChange={e=>setData({...data, laborRate: Number(e.target.value)})}/></Field>
        <Field label="Diagnostic Fee ($)"><Input type="number" step="0.01" value={data.diagnosticFee||89} onChange={e=>setData({...data, diagnosticFee: Number(e.target.value)})}/></Field>
        <Field label="Min Service Call ($)"><Input type="number" step="0.01" value={data.minimumServiceCall||89} onChange={e=>setData({...data, minimumServiceCall: Number(e.target.value)})}/></Field>
        <Field label="Tax Rate %"><Input type="number" step="0.1" value={data.taxRate||7} onChange={e=>setData({...data, taxRate: Number(e.target.value)})}/></Field>
        <Field label="Default COGS %" hint="Used for P&L margin calc"><Input type="number" step="1" value={data.cogsDefaultPct||35} onChange={e=>setData({...data, cogsDefaultPct: Number(e.target.value)})}/></Field>
        <Field label="Monthly Overhead ($)" hint="Rent + insurance + software"><Input type="number" step="100" value={data.overheadMonthly||0} onChange={e=>setData({...data, overheadMonthly: Number(e.target.value)})}/></Field>
      </div>
    </>}

    {step === 3 && <ServiceAreaStep data={data} setData={setData}/>}

    {step === 4 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>4️⃣ Customer-Facing Copy</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>What your customers see in estimates, invoices, and review requests.</div>
      <Field label="Estimate footer notes"><Textarea value={data.estimateNotes||''} onChange={e=>setData({...data, estimateNotes:e.target.value})} placeholder="This estimate is valid for 30 days. Prices subject to change after expiration."/></Field>
      <Field label="Invoice footer notes"><Textarea value={data.invoiceNotes||''} onChange={e=>setData({...data, invoiceNotes:e.target.value})} placeholder="Thank you for your business! Payment due within 30 days."/></Field>
      <Field label="Payment Terms">
        <Select value={data.paymentTerms||'Due on receipt'} onChange={e=>setData({...data, paymentTerms:e.target.value})}>
          {['Due on receipt','Net 7','Net 15','Net 30','Net 60'].map(t => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Google Review Link" hint="Where customers go when they leave a 4+ star review"><Input value={data.reviewLinkGoogle||''} onChange={e=>setData({...data, reviewLinkGoogle:e.target.value})} placeholder="https://g.page/r/..."/></Field>
    </>}

    {step === 5 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>5️⃣ Payments — Stripe Connect</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
        Connect your bank account through Stripe so customers can pay invoices by card or ACH. You'll be redirected to Stripe to verify your business and add a payout account. Takes 5–10 minutes.
      </div>
      {stripeStatus?.connected ? (
        <div style={{ ...styles.card, padding:18, borderColor:G.green+'66', background:G.greenLight }}>
          <div style={{ fontWeight:700, color:G.green, marginBottom:6 }}>✅ Stripe connected</div>
          <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.6 }}>
            Charges enabled: {stripeStatus.chargesEnabled ? 'yes' : 'no'} · Payouts enabled: {stripeStatus.payoutsEnabled ? 'yes' : 'no'}
            {(stripeStatus.requirementsDue?.length > 0 || stripeStatus.requirementsPastDue?.length > 0) && <div style={{ marginTop:6, color:G.yellow }}>⚠️ Outstanding requirements: {[...(stripeStatus.requirementsPastDue||[]), ...(stripeStatus.requirementsDue||[])].join(', ')} — complete in Stripe.</div>}
          </div>
        </div>
      ) : (
        <div style={{ ...styles.card, padding:18 }}>
          <div style={{ fontSize:14, marginBottom:14 }}>You'll need: business EIN, bank account number + routing, owner's SSN (for KYC), photo ID.</div>
          <Btn v="primary" onClick={startStripeOnboarding} disabled={busy}>{busy ? <Spinner sz={14}/> : '🚀 Launch Stripe Connect →'}</Btn>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:10 }}>Skip for now if you only want to send estimates/invoices without collecting payment.</div>
        </div>
      )}
    </>}

    {step === 6 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>6️⃣ SMS Verification (Toll-Free)</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
        Submits your business to Twilio for verification so carriers stop filtering your texts. Required before customers reliably receive your appointment reminders + on-the-way messages. Approval: 1–3 business days.
      </div>
      {!tfvStatus?.canSubmit && <div style={{ ...styles.card, padding:14, borderColor:G.yellow+'66', background:G.yellowLight, marginBottom:12, fontSize:13 }}>
        ⚠️ Finish Step 1 first — TFV needs legal name + EIN + business address.
      </div>}
      {tfvStatus?.status === 'pending_review' && <div style={{ ...styles.card, padding:14, borderColor:G.cyan+'66', background:G.cyanLight||G.input }}>
        ⏳ Submitted{tfvStatus.submittedAt ? ` ${new Date(tfvStatus.submittedAt).toLocaleDateString()}` : ''}. Twilio reviewing. You can still send SMS to verified test numbers while waiting.
      </div>}
      {tfvStatus?.status === 'approved' && <div style={{ ...styles.card, padding:14, borderColor:G.green+'66', background:G.greenLight, color:G.green }}>✅ Verified — full sending unlocked.</div>}
      {tfvStatus?.status === 'rejected' && <div style={{ ...styles.card, padding:14, borderColor:G.red+'66', background:G.redLight, color:G.red, marginBottom:12 }}>❌ Rejected — re-submit with corrected info below.</div>}
      {tfvStatus?.canSubmit && (tfvStatus.status === 'not_submitted' || tfvStatus.status === 'rejected') && <>
        <Field label="Use case category">
          <Select value={tfvForm.useCase} onChange={e=>setTfvForm({...tfvForm, useCase:e.target.value})}>
            <option value="CUSTOMER_CARE">Customer Care</option>
            <option value="ACCOUNT_NOTIFICATION">Account Notifications</option>
            <option value="DELIVERY_NOTIFICATION">Delivery / Dispatch Notifications</option>
            <option value="2FA">2-Factor Auth / OTP</option>
            <option value="MARKETING">Marketing</option>
          </Select>
        </Field>
        <Field label="Sample message" hint="Twilio reviews this. Include the business name + STOP language.">
          <Textarea value={tfvForm.sampleMsg} onChange={e=>setTfvForm({...tfvForm, sampleMsg:e.target.value})}
            placeholder={`Hi {first_name}, this is ${data.name||'<business>'} confirming your appointment tomorrow at 2 PM. Reply STOP to opt out.`}/>
        </Field>
        <Field label="Opt-in screenshot URL" hint="Optional but strongly recommended — screenshot of where customers consent. Speeds approval.">
          <Input value={tfvForm.optInUrl} onChange={e=>setTfvForm({...tfvForm, optInUrl:e.target.value})} placeholder="https://yoursite.com/contact"/>
        </Field>
        <Btn v="primary" onClick={submitTfv} disabled={busy || !tfvForm.sampleMsg.trim()}>{busy ? <Spinner sz={14}/> : '📤 Submit to Twilio'}</Btn>
      </>}
    </>}

    {step === 7 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>7️⃣ Team Members</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
        Add your dispatchers and field techs so they can log into the mobile shell + receive dispatch notifications. The owner account (you) is already set.
      </div>

      {team.length > 0 && <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:6 }}>Current team ({team.length})</div>
        {team.map(u => <div key={u.id} style={{ ...styles.card, padding:10, marginBottom:4, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <strong>{u.firstName} {u.lastName}</strong>
            <span style={{ color:G.textMuted, fontSize:12, marginLeft:8 }}>{u.email}</span>
          </div>
          <Badge c={G.purple} bg={G.purpleDim}>{u.role}</Badge>
        </div>)}
      </div>}

      <div style={{ ...styles.card, padding:14 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>+ Add a team member</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
          <Field label="First name *"><Input value={newMember.firstName} onChange={e=>setNewMember({...newMember, firstName:e.target.value})}/></Field>
          <Field label="Last name"><Input value={newMember.lastName} onChange={e=>setNewMember({...newMember, lastName:e.target.value})}/></Field>
          <Field label="Email *"><Input type="email" value={newMember.email} onChange={e=>setNewMember({...newMember, email:e.target.value})}/></Field>
          <Field label="Mobile phone"><Input value={newMember.phone} onChange={e=>setNewMember({...newMember, phone:e.target.value})}/></Field>
          <Field label="Role">
            <Select value={newMember.role} onChange={e=>setNewMember({...newMember, role:e.target.value})}>
              <option value="tech">Field tech (mobile app)</option>
              <option value="dispatcher">Dispatcher (web)</option>
              <option value="admin">Admin (full access)</option>
              <option value="owner">Owner (full access)</option>
            </Select>
          </Field>
        </div>
        <Btn v="primary" onClick={addTeamMember} disabled={busy || !newMember.email || !newMember.firstName}>{busy ? <Spinner sz={14}/> : '+ Add member'}</Btn>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>They'll get a temporary password shown on screen — text it to them along with the login URL.</div>
      </div>
    </>}

    {step === 8 && <TermsStep data={data} setData={setData} toast={toast}/>}

    {step === 9 && <>
      <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>9️⃣ Go Live</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
        Quick review — anything red is worth fixing before you start sending real customer texts/emails. Once you hit Activate, we kick off welcome flows + auto-apply your industry snapshot.
      </div>
      <div style={{ ...styles.card, padding:14 }}>
        {[
          { label:'Business profile + EIN + address', ok: step1Valid, step:1 },
          { label:'Services + pricing set', ok: step2Valid, step:2 },
          { label:'Service area zips', ok: step3Valid, step:3 },
          { label:'Stripe Connect connected', ok: !!stripeStatus?.connected, step:5, optional:true },
          { label:'Toll-free SMS verification submitted', ok: !!(tfvStatus?.status && tfvStatus.status !== 'not_submitted'), step:6, optional:true },
          { label:`Team set up (${team.length} member${team.length===1?'':'s'})`, ok: team.length >= 1, step:7, optional:true },
        ].map((c,i) => <div key={i} onClick={()=>setStep(c.step)} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 4px', cursor:'pointer', borderBottom:`1px solid ${G.cardBorder}` }}>
          <span style={{ fontSize:18, color: c.ok ? G.green : (c.optional ? G.textMuted : G.red) }}>{c.ok ? '✓' : (c.optional ? '○' : '✗')}</span>
          <span style={{ flex:1, fontSize:14, color: c.ok ? G.text : G.textMuted }}>{c.label}{c.optional && !c.ok ? ' (optional)' : ''}</span>
          <span style={{ fontSize:12, color:G.purple }}>{c.ok ? '' : 'Fix →'}</span>
        </div>)}
      </div>
      <div style={{ marginTop:14, padding:14, background:G.purpleDim, borderRadius:10, fontSize:13, lineHeight:1.55 }}>
        After Activate: we'll send the welcome email, fire a Slack/webhook ping, pre-fill Stripe customer data, and apply your industry snapshot (default workflows, scripts, and templates for {data.industry || 'your trade'}).
      </div>
    </>}

    <div style={{ display:'flex', justifyContent:'space-between', marginTop:18 }}>
      <Btn onClick={async ()=>{ await save(false); onClose(); }} disabled={busy}>Save & Close</Btn>
      <div style={{ display:'flex', gap:10 }}>
        {step > 1 && <Btn onClick={()=>setStep(step-1)}>← Back</Btn>}
        {step < TOTAL_STEPS && <Btn v="primary" onClick={async ()=>{ await save(false); setStep(step+1); }} disabled={busy || !stepValid(step)}>Next →</Btn>}
        {step === TOTAL_STEPS && <Btn v="primary" onClick={()=>save(true)} disabled={busy || !step1Valid || !data.saasAgreementAcceptedAt}>{busy?<Spinner sz={14}/>:'🎉 Activate Account'}</Btn>}
      </div>
    </div>
  </Modal>;
}

// Terms step — captures TWO things:
//   1. The owner's acceptance of TradeOps' SaaS agreement (Master Services
//      Agreement). Stored as a timestamp + IP for our own audit trail.
//   2. The owner's OWN customer-facing T&C document — either upload a PDF
//      they already use OR accept our templated version that auto-populates
//      every signed estimate the customer approves.
function TermsStep({ data, setData, toast }) {
  const [uploading, setUploading] = useState(false);
  const fileInput = React.useRef(null);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast('File must be under 8MB', 'error'); return; }
    setUploading(true);
    try {
      // Read as base64 and POST — same pattern as the logo upload step.
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const r = await api('/api/admin-panel/upload-customer-terms', {
        method: 'POST',
        body: { filename: f.name, mime: f.type, base64: dataUrl.split(',')[1] || dataUrl },
      });
      setData({ ...data, customerTermsUrl: r.url, customerTermsFilename: f.name });
      toast('✓ Customer T&C uploaded');
    } catch (err) { toast(err.message, 'error'); }
    finally { setUploading(false); fileInput.current && (fileInput.current.value = ''); }
  };
  return <>
    <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>8️⃣ Terms &amp; Agreements</h3>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Two quick agreements — one with TradeOps (us), one for your own customers.
    </div>

    {/* TradeOps SaaS agreement */}
    <div style={{ ...styles.card, padding:16, marginBottom:14, borderColor: data.saasAgreementAcceptedAt ? G.green+'66' : G.cardBorder }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>1. Your agreement with TradeOps</div>
      <div style={{ fontSize:14, marginTop:6, marginBottom:10, lineHeight:1.55 }}>
        Before we activate your account, please review and accept our SaaS Master Services Agreement.
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <Btn sz="sm" onClick={()=>window.open('/lp/terms','_blank')}>📄 Read TradeOps Terms</Btn>
        <Btn sz="sm" onClick={()=>window.open('/lp/privacy','_blank')}>🔒 Privacy Policy</Btn>
      </div>
      <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer', fontSize:13, lineHeight:1.5 }}>
        <input type="checkbox"
          checked={!!data.saasAgreementAcceptedAt}
          onChange={e=>setData({ ...data, saasAgreementAcceptedAt: e.target.checked ? new Date().toISOString() : null })}
          style={{ marginTop:3, width:18, height:18, flexShrink:0, cursor:'pointer' }}/>
        <span>
          <strong>I accept</strong> the TradeOps Master Services Agreement and Privacy Policy, on behalf of <strong>{data.legalName || data.name || '(my business)'}</strong>, and confirm I'm authorized to bind the business.
        </span>
      </label>
      {data.saasAgreementAcceptedAt && <div style={{ fontSize:11, color:G.green, marginTop:8 }}>
        ✓ Accepted on {new Date(data.saasAgreementAcceptedAt).toLocaleString()}
      </div>}
    </div>

    {/* Customer-facing T&C */}
    <div style={{ ...styles.card, padding:16 }}>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>2. Your customer-facing Terms &amp; Conditions</div>
      <div style={{ fontSize:14, marginTop:6, marginBottom:10, lineHeight:1.55 }}>
        Every estimate your customer approves links to YOUR T&C. Choose how you want to handle it:
      </div>

      <div style={{ padding:12, borderRadius:8, background: data.customerTermsMode === 'template' ? G.purpleDim : G.input, border:`1px solid ${data.customerTermsMode === 'template' ? G.purple+'66' : G.cardBorder}`, marginBottom:10, cursor:'pointer' }}
        onClick={()=>setData({ ...data, customerTermsMode: 'template' })}>
        <strong style={{ fontSize:13 }}>Option A · Use TradeOps' template</strong>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
          We auto-generate a clean Terms page at <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>/lp/a/{`<slug>`}/terms</code> with your business name, warranty, payment terms, and SMS clauses. Most customers use this.
        </div>
      </div>

      <div style={{ padding:12, borderRadius:8, background: data.customerTermsMode === 'upload' ? G.purpleDim : G.input, border:`1px solid ${data.customerTermsMode === 'upload' ? G.purple+'66' : G.cardBorder}`, cursor:'pointer' }}
        onClick={()=>setData({ ...data, customerTermsMode: 'upload' })}>
        <strong style={{ fontSize:13 }}>Option B · Upload your own T&C document</strong>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
          Have one your lawyer drafted? Upload PDF/Word — we'll attach it to every signed estimate and link to it from your Terms URL.
        </div>
        {data.customerTermsMode === 'upload' && <div style={{ marginTop:10 }}>
          {data.customerTermsUrl ? (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:G.greenLight, borderRadius:6 }}>
              <span style={{ color:G.green }}>✓</span>
              <a href={data.customerTermsUrl} target="_blank" rel="noreferrer" style={{ flex:1, color:G.green, fontSize:13, textDecoration:'underline' }}>{data.customerTermsFilename || 'Open document'}</a>
              <Btn sz="xs" v="ghost" onClick={(e)=>{ e.stopPropagation(); fileInput.current?.click(); }}>Replace</Btn>
            </div>
          ) : (
            <Btn sz="sm" v="primary" onClick={(e)=>{ e.stopPropagation(); fileInput.current?.click(); }} disabled={uploading}>
              {uploading ? <Spinner sz={12}/> : '📤 Upload PDF or Word doc'}
            </Btn>
          )}
          <input ref={fileInput} type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={onUpload} style={{ display:'none' }}/>
          <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>Max 8MB. PDF preferred.</div>
        </div>}
      </div>
    </div>
  </>;
}

// Service area step — three independent filters that combine to define the
// service footprint: explicit zip chips, mile radius around the business
// address, and a multi-select of state codes. Dispatch + ad targeting will
// respect any contact whose location matches at least one.
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],
  ['DE','Delaware'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],
  ['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],
  ['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],
  ['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],['DC','D.C.'],
];

function ServiceAreaStep({ data, setData }) {
  const [zipDraft, setZipDraft] = React.useState('');
  const zips = data.targetZips || [];
  const states = data.serviceStates || [];

  const addZip = (raw) => {
    const cleaned = String(raw || '').replace(/[^\d\s,]/g,'').split(/[\s,]+/).map(s=>s.trim()).filter(s => /^\d{5}(-\d{4})?$/.test(s));
    if (!cleaned.length) return;
    const merged = Array.from(new Set([...zips, ...cleaned]));
    setData({ ...data, targetZips: merged });
    setZipDraft('');
  };
  const removeZip = (z) => setData({ ...data, targetZips: zips.filter(x => x !== z) });
  const toggleState = (code) => {
    const next = states.includes(code) ? states.filter(c => c !== code) : [...states, code];
    setData({ ...data, serviceStates: next });
  };
  const onZipKey = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addZip(zipDraft);
    } else if (e.key === 'Backspace' && !zipDraft && zips.length) {
      setData({ ...data, targetZips: zips.slice(0, -1) });
    }
  };
  const onZipPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (/[,\s]/.test(text)) { e.preventDefault(); addZip(text); }
  };

  return <>
    <h3 style={{ margin:'0 0 6px 0', fontSize:15 }}>3️⃣ Service Area + Hours</h3>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Define where you work using any combination of <strong>specific ZIPs</strong>, <strong>mile radius</strong> from your address, or <strong>whole states</strong>. Used for smart dispatch, customer SMS routing, ad geo-targeting, and "out of service area" auto-replies.
    </div>

    {/* Multi-zip chip input */}
    <Field label={`Specific ZIP codes (${zips.length})`} hint="Type a ZIP and press Enter, comma, Tab, or paste a list. Backspace removes the last.">
      <div style={{ ...styles.inp, display:'flex', flexWrap:'wrap', gap:6, padding:'8px', minHeight:44, cursor:'text' }} onClick={e=>{ if (e.target === e.currentTarget) e.currentTarget.querySelector('input')?.focus(); }}>
        {zips.map(z => <span key={z} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:14, background:G.purpleDim, color:G.purple, fontSize:13, fontWeight:600 }}>
          {z}
          <span onClick={()=>removeZip(z)} style={{ cursor:'pointer', fontSize:14, lineHeight:1, opacity:.7 }}>×</span>
        </span>)}
        <input
          value={zipDraft}
          onChange={e=>setZipDraft(e.target.value)}
          onKeyDown={onZipKey}
          onPaste={onZipPaste}
          onBlur={()=>{ if (zipDraft.trim()) addZip(zipDraft); }}
          placeholder={zips.length ? '' : '33601, 33602, 33606…'}
          style={{ flex:'1 1 120px', minWidth:120, border:'none', outline:'none', background:'transparent', color:G.text, fontSize:14, padding:'2px 4px' }}
        />
      </div>
    </Field>

    {/* Mile radius */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:4 }}>
      <Field label="Mile radius from your address (alt)" hint="e.g. 25 miles. Use 0 / blank to skip and rely on ZIPs/states.">
        <Input type="number" min="0" max="500" value={data.serviceRadiusMiles||''} onChange={e=>setData({...data, serviceRadiusMiles: e.target.value === '' ? null : Number(e.target.value)})} placeholder="25"/>
      </Field>
      <Field label="Service area description" hint="Human-readable name shown on website + estimates">
        <Input value={data.serviceArea||''} onChange={e=>setData({...data, serviceArea:e.target.value})} placeholder="Tampa Bay area"/>
      </Field>
    </div>

    {/* Multi-state */}
    <Field label={`States served (${states.length})`} hint="Click to toggle. Adding a whole state implicitly includes every ZIP in it.">
      <div style={{ display:'flex', flexWrap:'wrap', gap:4, padding:'4px 0' }}>
        {US_STATES.map(([code, name]) => {
          const on = states.includes(code);
          return <span key={code} title={name} onClick={()=>toggleState(code)} style={{
            padding:'5px 10px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600,
            border:`1px solid ${on?G.purple:G.cardBorder}`,
            background: on?G.purpleDim:'transparent',
            color: on?G.purple:G.textMuted,
          }}>{code}</span>;
        })}
      </div>
    </Field>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <Field label="Default Job Duration (minutes)"><Input type="number" value={data.defaultJobDurationMinutes||120} onChange={e=>setData({...data, defaultJobDurationMinutes: Number(e.target.value)})}/></Field>
      <Field label="Booking Buffer (minutes between jobs)"><Input type="number" value={data.bookingBufferMinutes||30} onChange={e=>setData({...data, bookingBufferMinutes: Number(e.target.value)})}/></Field>
    </div>

    <BusinessHoursEditor data={data} setData={setData}/>
  </>;
}

// Business hours editor — supports multiple time blocks per day (e.g. open
// 07:00–12:00 and again 13:00–17:00 to skip lunch). Backwards-compatible with
// the legacy single-range string format: any string value gets parsed into a
// 1-block array on render and saved back as the array form.
function parseBlocks(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(b => b && b.from && b.to);
  if (typeof v === 'string') {
    const m = v.match(/^\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\s*$/);
    if (m) return [{ from: m[1], to: m[2] }];
  }
  return [];
}
function blocksFor(hours, dayKey) {
  return parseBlocks((hours||{})[dayKey]);
}

function BusinessHoursEditor({ data, setData }) {
  const hours = data.businessHours || {};
  const setDay = (k, blocks) => setData({ ...data, businessHours: { ...(data.businessHours||{}), [k]: blocks.length ? blocks : null }});
  const addBlock = (k) => setDay(k, [...blocksFor(hours, k), { from:'09:00', to:'17:00' }]);
  const removeBlock = (k, i) => setDay(k, blocksFor(hours, k).filter((_,j) => j !== i));
  const updateBlock = (k, i, key, val) => {
    const arr = blocksFor(hours, k).slice();
    arr[i] = { ...arr[i], [key]: val };
    setDay(k, arr);
  };
  const copyMonToWeekdays = () => {
    const monBlocks = blocksFor(hours, 'mon');
    const next = { ...hours };
    ['tue','wed','thu','fri'].forEach(k => { next[k] = monBlocks; });
    setData({ ...data, businessHours: next });
  };
  const copyMonToAll = () => {
    const monBlocks = blocksFor(hours, 'mon');
    const next = { ...hours };
    DAYS.forEach(([k]) => { next[k] = monBlocks; });
    setData({ ...data, businessHours: next });
  };

  return <Field label="Business Hours" hint="Add multiple time blocks to a day to skip lunch or run split shifts. Leave a day with no blocks to mark it closed.">
    <div style={{ display:'flex', gap:6, marginBottom:8 }}>
      <Btn sz="xs" onClick={copyMonToWeekdays}>Copy Mon → Tue-Fri</Btn>
      <Btn sz="xs" onClick={copyMonToAll}>Copy Mon → All 7 days</Btn>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:8 }}>
      {DAYS.map(([k,l]) => {
        const blocks = blocksFor(hours, k);
        const closed = blocks.length === 0;
        return <div key={k} style={{ ...styles.card, padding:10 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
            <strong style={{ fontSize:12, color:closed ? G.textMuted : G.text }}>{l}</strong>
            {closed
              ? <Btn sz="xs" onClick={()=>addBlock(k)}>+ Open</Btn>
              : <Btn sz="xs" onClick={()=>addBlock(k)}>+ Block</Btn>
            }
          </div>
          {closed && <div style={{ fontSize:11, color:G.textMuted, fontStyle:'italic' }}>Closed</div>}
          {blocks.map((b, i) => <div key={i} style={{ display:'flex', alignItems:'center', gap:4, marginTop:4 }}>
            <Input type="time" value={b.from||''} onChange={e=>updateBlock(k,i,'from',e.target.value)} style={{ fontSize:11, padding:'3px 6px' }}/>
            <span style={{ color:G.textMuted, fontSize:11 }}>–</span>
            <Input type="time" value={b.to||''} onChange={e=>updateBlock(k,i,'to',e.target.value)} style={{ fontSize:11, padding:'3px 6px' }}/>
            {blocks.length > 0 && <span onClick={()=>removeBlock(k,i)} title="Remove block" style={{ cursor:'pointer', color:G.textMuted, fontSize:14, padding:'0 4px' }}>×</span>}
          </div>)}
        </div>;
      })}
    </div>
  </Field>;
}
